/**
 * The customers whose remittances MarginWiz reads out of a connected mailbox — the payers behind
 * the `email-remittance` connection, shown as their own wall on the connections page.
 *
 * Every entry is a payer the reader has booked at least one payment for. The list is what a brand
 * scans to answer "do you cover my customer?", so it names the company the brand sells to, not the
 * format or the processor: Serv-U-Success's notices are Meijer's payments, `GO BRANDS INC` is
 * Gopuff, `Grocery Delivery E-Services` is HelloFresh, Demoula's is Market Basket.
 *
 * Customers with a card of their own (C&S, UNFI, Hy-Vee, Albertsons, Crown Pacific, …) are left
 * out even where a scanned check of theirs has been read from mail: the card is where they connect.
 *
 * Logos live in `/img/integration/email/`, trimmed from each company's own site or its Wikipedia
 * logo file and saved as WebP at twice the size a tile shows them (188x72 at most; the four that
 * are also card logos at 500x112), so the whole wall is under 200 KB. A payer without one renders
 * as its name.
 */
export interface EmailSource {
  id: string
  name: string
  kind: "distributor" | "retailer"
  logo?: string
  /** Extra lowercase search terms: the name the remittance prints, a banner, an abbreviation. */
  aliases?: string[]
}

function logo(file: string): string {
  return `/img/integration/email/${file}.webp`
}

export const emailSources: EmailSource[] = [
  { id: "mclane", name: "McLane", kind: "distributor", logo: logo("mclane"), aliases: ["mclaneco"] },
  { id: "stater-bros", name: "Stater Bros. Markets", kind: "retailer", logo: logo("stater-bros"), aliases: ["stater brothers"] },
  { id: "grocery-supply", name: "Grocery Supply Company", kind: "distributor", logo: logo("grocery-supply"), aliases: ["gsc"] },
  { id: "caseys", name: "Casey's", kind: "retailer", logo: logo("caseys"), aliases: ["caseys", "casey's general stores"] },
  { id: "academy", name: "Academy Sports + Outdoors", kind: "retailer", logo: logo("academy") },
  { id: "meijer", name: "Meijer", kind: "retailer", logo: logo("meijer"), aliases: ["serv-u-success", "serv u success"] },
  { id: "circle-k", name: "Circle K", kind: "retailer", logo: logo("circle-k"), aliases: ["circlek", "couche-tard"] },
  { id: "wawa", name: "Wawa", kind: "retailer", logo: logo("wawa") },
  { id: "five-below", name: "Five Below", kind: "retailer", logo: logo("five-below"), aliases: ["fivebelow", "1616 holdings"] },
  { id: "market-basket", name: "Market Basket", kind: "retailer", logo: logo("market-basket"), aliases: ["demoulas", "demoula's"] },
  { id: "gopuff", name: "Gopuff", kind: "retailer", logo: logo("gopuff"), aliases: ["go brands", "gobrands"] },
  { id: "hellofresh", name: "HelloFresh", kind: "retailer", logo: logo("hellofresh"), aliases: ["grocery delivery e-services", "hello fresh"] },
  { id: "weis", name: "Weis Markets", kind: "retailer", logo: logo("weis") },
  { id: "fresh-thyme", name: "Fresh Thyme Market", kind: "retailer", logo: logo("fresh-thyme"), aliases: ["freshthyme"] },
  { id: "redners", name: "Redner's Markets", kind: "retailer", logo: logo("redners"), aliases: ["redners"] },
  { id: "ocado", name: "Ocado", kind: "retailer", logo: logo("ocado"), aliases: ["ocado retail"] },
  { id: "holland-barrett", name: "Holland & Barrett", kind: "retailer", logo: logo("holland-barrett"), aliases: ["h&b", "holland and barrett"] },
  { id: "bi-mart", name: "Bi-Mart", kind: "retailer", logo: logo("bi-mart"), aliases: ["bimart"] },
  { id: "merchants-distributors", name: "Merchants Distributors", kind: "distributor", logo: logo("merchants-distributors"), aliases: ["mdi"] },
  { id: "big-geyser", name: "Big Geyser", kind: "distributor", logo: logo("big-geyser") },
  { id: "heidelberg", name: "Heidelberg Distributing", kind: "distributor", logo: logo("heidelberg") },
  { id: "glazers-beer", name: "Glazer's Beer & Beverage", kind: "distributor", logo: logo("glazers-beer"), aliases: ["glazers"] },
  { id: "dynamo", name: "Dynamo Specialty Distributing", kind: "distributor", logo: logo("dynamo") },
  { id: "yumi", name: "Yumi Ice Cream", kind: "distributor", logo: logo("yumi") },
  { id: "wright-beverage", name: "Wright Beverage Distributing", kind: "distributor", logo: logo("wright-beverage") },
  { id: "bellavance", name: "Bellavance Beverage", kind: "distributor", logo: logo("bellavance") },
  { id: "glacier-point", name: "GlacierPoint Enterprises", kind: "distributor", logo: logo("glacier-point"), aliases: ["glacier point"] },
  { id: "certco", name: "Certco", kind: "distributor", logo: logo("certco") },
  { id: "woodland-partners", name: "Woodland Partners", kind: "distributor" },
  { id: "john-lenore", name: "John Lenore & Co.", kind: "distributor", aliases: ["john lenore"] },
  { id: "national-convenience", name: "National Convenience Distributors", kind: "distributor", aliases: ["ncd"] },
  { id: "eight-point", name: "Eight Point Distributors", kind: "distributor", logo: logo("eight-point") },
  { id: "summit-beverage", name: "Summit Beverage", kind: "distributor", logo: logo("summit-beverage"), aliases: ["george's distributing"] },
  { id: "sunset", name: "Sunset Distributing", kind: "distributor", logo: logo("sunset") },
  { id: "heimark", name: "Heimark Distributing", kind: "distributor", logo: logo("heimark") },
  { id: "jomast", name: "Jomast", kind: "distributor" },
  { id: "halsted", name: "Halsted", kind: "distributor" },
]

/** Sources matching a search, by name or alias. An empty query matches nothing: the wall shows all. */
export function search_email_sources(sources: EmailSource[], query: string): EmailSource[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return []
  return sources.filter(
    (source: EmailSource) =>
      source.name.toLowerCase().includes(needle) || (source.aliases ?? []).some((alias: string) => alias.includes(needle))
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("email_sources", () => {
    it("finds a payer by the name its remittance prints, not only the one on the tile", () => {
      expect(search_email_sources(emailSources, "demoulas").map(s => s.id)).toEqual(["market-basket"])
      expect(search_email_sources(emailSources, "Serv-U").map(s => s.id)).toEqual(["meijer"])
      expect(search_email_sources(emailSources, "go brands").map(s => s.id)).toEqual(["gopuff"])
      expect(search_email_sources(emailSources, "  ")).toEqual([])
      expect(search_email_sources(emailSources, "zzz")).toEqual([])
    })

    it("has no duplicate ids and keeps aliases lowercase so the search can match them", () => {
      const ids = emailSources.map(s => s.id)
      expect(new Set(ids).size).toBe(ids.length)
      for (const source of emailSources) {
        for (const alias of source.aliases ?? []) expect(alias).toBe(alias.toLowerCase())
      }
    })
  })
}
